/**
 * Specular first-reflection geometry + comb-filter interference.
 * Extends the simple mirror-point math already in acousticsPhysics.
 */

import { SPEED_OF_SOUND_MPS, calculateFirstReflections } from '@/lib/acousticsPhysics';

export interface MirrorGeometryInput {
  lengthM: number;
  widthM: number;
  heightM: number;
  speakerDistFrontM: number;
  speakerSideDistM: number;
  listenerDistFrontM: number;
  /** Listener height (ear), default seated ~1.2 m. */
  listenerHeightM?: number;
  /** Speaker height (tweeter), default ~1.2 m. */
  speakerHeightM?: number;
}

export interface MirrorPoint {
  surface: 'left' | 'right' | 'ceiling' | 'floor';
  /** Distance from front wall along room length (m). */
  distFromFrontM: number;
  /** Height on wall / lateral position on ceiling-floor (m). */
  secondaryM: number;
  pathLengthM: number;
  delayMs: number;
}

export interface CombFilterResult {
  mirrors: MirrorPoint[];
  /** Dominant early reflection (shortest delay < 15 ms preferred). */
  primary: MirrorPoint | null;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Mirror-point coordinates + path delay for side walls, ceiling, and floor.
 * Side-wall Y uses the existing specular ratio from calculateFirstReflections.
 */
export function calculateMirrorGeometry(input: MirrorGeometryInput): CombFilterResult {
  const {
    lengthM,
    widthM,
    heightM,
    speakerDistFrontM,
    speakerSideDistM,
    listenerDistFrontM,
    listenerHeightM = 1.2,
    speakerHeightM = 1.2,
  } = input;

  const base = calculateFirstReflections(
    lengthM,
    widthM,
    heightM,
    speakerDistFrontM,
    speakerSideDistM,
    listenerDistFrontM,
  );

  const listenerX = widthM / 2;
  const speakerX = speakerSideDistM;
  const speakerY = speakerDistFrontM;
  const listenerY = listenerDistFrontM;

  const path = (ax: number, ay: number, az: number, bx: number, by: number, bz: number) =>
    Math.hypot(ax - bx, ay - by, az - bz);

  const make = (
    surface: MirrorPoint['surface'],
    hitX: number,
    hitY: number,
    hitZ: number,
  ): MirrorPoint => {
    const toHit = path(speakerX, speakerY, speakerHeightM, hitX, hitY, hitZ);
    const toEar = path(hitX, hitY, hitZ, listenerX, listenerY, listenerHeightM);
    const pathLengthM = toHit + toEar;
    const direct = path(speakerX, speakerY, speakerHeightM, listenerX, listenerY, listenerHeightM);
    const delayMs = Math.max(0, ((pathLengthM - direct) / SPEED_OF_SOUND_MPS) * 1000);
    return {
      surface,
      distFromFrontM: round2(hitY),
      secondaryM: round2(surface === 'ceiling' || surface === 'floor' ? hitX : hitZ),
      pathLengthM: round2(pathLengthM),
      delayMs: Math.round(delayMs * 10) / 10,
    };
  };

  const leftY = base.leftWallDistFromFrontM;
  const rightY = base.rightWallDistFromFrontM;
  const ceilY = base.ceilingDistFromFrontM;

  // Ceiling / floor hit X from similar-triangles in the vertical plane.
  const vertSpan = Math.abs(listenerHeightM - speakerHeightM) || 0.01;
  const ceilRatio = (heightM - speakerHeightM) / (2 * heightM - speakerHeightM - listenerHeightM || vertSpan);
  const floorRatio = speakerHeightM / (speakerHeightM + listenerHeightM || vertSpan);
  const ceilX = speakerX + ceilRatio * (listenerX - speakerX);
  const floorX = speakerX + floorRatio * (listenerX - speakerX);

  const mirrors: MirrorPoint[] = [
    make('left', 0, leftY, (speakerHeightM + listenerHeightM) / 2),
    make('right', widthM, rightY, (speakerHeightM + listenerHeightM) / 2),
    make('ceiling', ceilX, ceilY, heightM),
    make('floor', floorX, ceilY, 0),
  ];

  const early = mirrors.filter((m) => m.delayMs > 0 && m.delayMs < 15);
  const pool = early.length > 0 ? early : mirrors;
  const primary = pool.reduce((a, b) => (a.delayMs < b.delayMs ? a : b), pool[0] ?? null);

  return { mirrors, primary };
}

/**
 * Comb-filter linear magnitude: |1 + r · e^(−j 2π f τ)|.
 * When absorbed, reflection coefficient collapses toward ~0.15.
 */
export function combMagnitudeResponse(
  freqs: number[],
  delaySec: number,
  reflectionCoef: number,
): number[] {
  const r = Math.max(0, Math.min(0.95, reflectionCoef));
  return freqs.map((f) => {
    const phase = 2 * Math.PI * f * delaySec;
    const re = 1 + r * Math.cos(phase);
    const im = -r * Math.sin(phase);
    return Math.hypot(re, im);
  });
}

export function reflectionCoefForSurface(surface: MirrorPoint['surface'], panelOn: boolean): number {
  if (panelOn) return 0.12;
  switch (surface) {
    case 'floor':
      return 0.55;
    case 'ceiling':
      return 0.7;
    default:
      return 0.75;
  }
}
